import type { ReactNode } from 'react';
import type { FieldSchema } from '../types';
import type { Question } from '../models/Question';

export interface QuestionWrapperProps {
  question: Question;
  fieldSchema: FieldSchema;
  show: boolean;
  showValidationMsg: boolean;
  validationMessage: string;
  alertMsg?: string;
  helpTextOpen: boolean;
  toggleQuestionHelpText: () => void;
  questionHelpTextId: string;
  helpTextExtraClass: string;
  useLegend?: boolean;
  children: ReactNode;
  extraValidation?: ReactNode;
}

export function QuestionWrapper({
  question,
  fieldSchema,
  show,
  showValidationMsg,
  validationMessage,
  alertMsg,
  helpTextOpen,
  toggleQuestionHelpText,
  questionHelpTextId,
  helpTextExtraClass,
  useLegend = false,
  children,
  extraValidation,
}: QuestionWrapperProps) {
  if (!show) {
    return null;
  }

  const LabelTag = useLegend ? 'legend' : 'label';
  const labelProps = useLegend
    ? { id: `label-q-${question.label}` }
    : { id: `label-q-${question.id}`, htmlFor: `input-${question.id}` };

  return (
    <div className={showValidationMsg ? 'error-active' : undefined}>
      <hr className="pull-right" />

      <div className="stepform-question-wrapper" id={`field-${question.id}`}>
        <div className="question table">
          <LabelTag {...labelProps}>
            {question.label}{' '}
            {question.optional && <span className="optional">(optional)</span>}
          </LabelTag>
          {fieldSchema.helpText && (
            <div className="help-toggle-wrapper">
              <button
                type="button"
                onClick={toggleQuestionHelpText}
                aria-controls={questionHelpTextId}
                aria-expanded={helpTextOpen}
              >
                ?<span className="visuallyhidden">Show/Hide supporting help text</span>
              </button>
            </div>
          )}
        </div>

        {fieldSchema.supportText && (
          <div
            className="qa-support-text"
            dangerouslySetInnerHTML={{ __html: fieldSchema.supportText }}
          />
        )}

        {fieldSchema.helpText && (
          <div
            className={`a-help qa-help-text pull-right ${helpTextExtraClass}${helpTextOpen ? ' open' : ''}`}
            id={questionHelpTextId}
            aria-labelledby={`label-q-${question.id}`}
            aria-hidden={!helpTextOpen && !fieldSchema.openHelpText}
          >
            <div className="outer">
              <div className="inner" dangerouslySetInnerHTML={{ __html: fieldSchema.helpText }} />
            </div>
          </div>
        )}

        <div
          className={`validation-msg${showValidationMsg ? ' active' : ''}`}
          style={{ display: showValidationMsg ? undefined : 'none' }}
        >
          {validationMessage}
        </div>
        {extraValidation}
        {alertMsg && <div className="alert-msg">{alertMsg}</div>}

        {children}
      </div>
    </div>
  );
}

export interface FieldsetWrapperProps extends Omit<QuestionWrapperProps, 'useLegend'> {
  answered?: boolean;
}

export function FieldsetWrapper({ answered, children, ...props }: FieldsetWrapperProps) {
  if (!props.show) {
    return null;
  }

  return (
    <div className={props.showValidationMsg ? 'error-active' : undefined}>
      <hr className="pull-right" />
      <fieldset
        className={`stepform-question-wrapper${answered ? ' answered' : ''}`}
        id={`field-${props.question.id}`}
      >
        <div className="question table">
          <legend id={`label-q-${props.question.label}`}>
            {props.question.label}{' '}
            {props.question.optional && <span className="optional">(optional)</span>}
          </legend>
          {props.fieldSchema.helpText && (
            <div className="help-toggle-wrapper">
              <button
                type="button"
                onClick={props.toggleQuestionHelpText}
                aria-controls={props.questionHelpTextId}
                aria-expanded={props.helpTextOpen}
              >
                ?<span className="visuallyhidden">Show/Hide supporting help text</span>
              </button>
            </div>
          )}
        </div>

        {props.fieldSchema.supportText && (
          <div
            className="qa-support-text"
            dangerouslySetInnerHTML={{ __html: props.fieldSchema.supportText }}
          />
        )}

        {props.fieldSchema.helpText && (
          <div
            className={`q-help qa-help-text pull-right ${props.helpTextExtraClass}${props.helpTextOpen ? ' open' : ''}`}
            id={props.questionHelpTextId}
            aria-labelledby={`label-q-${props.question.id}`}
          >
            <div className="outer">
              <div
                className="inner"
                dangerouslySetInnerHTML={{ __html: props.fieldSchema.helpText }}
              />
            </div>
          </div>
        )}

        <div
          className={`validation-msg${props.showValidationMsg ? ' active' : ''}`}
          style={{ display: props.showValidationMsg ? undefined : 'none' }}
        >
          {props.validationMessage}
        </div>
        {props.extraValidation}
        {props.alertMsg && <div className="alert-msg">{props.alertMsg}</div>}

        {children}
      </fieldset>
    </div>
  );
}
